import { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import { getRequest, postRequest } from "../../../utils/api";
import { ApiInvoice, inr2, invoiceNo } from "../invoiceUtils";

interface Props {
  invoice: ApiInvoice | null;
  show: boolean;
  onHide: () => void;
  onApproved?: () => void;
}

// Shows what commission this invoice would earn, then approves it on click.
// Nothing is written until Approve; the server re-checks everything on approve.
const InvoiceCommissionModal = ({ invoice, show, onHide, onApproved }: Props) => {
  const [preview, setPreview] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!show || !invoice) return;
    setPreview(null);
    setLoading(true);
    getRequest(`/subscription/invoice-commission/${invoice._id}`)
      .then((res: any) => {
        if (res.status === "success") setPreview(res.data);
        else toast.error(res.message || "Could not load commission");
      })
      .catch(() => toast.error("Could not load commission"))
      .finally(() => setLoading(false));
  }, [show, invoice]);

  const approve = async () => {
    if (!invoice) return;
    setLoading(true);
    try {
      const res: any = await postRequest(`/subscription/invoice-commission/${invoice._id}/approve`, {});
      if (res.status === "success") {
        toast.success("Commission approved");
        onApproved?.();
        onHide();
      } else {
        toast.error(res.message || "Commission not created");
      }
    } catch {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const pct = (n: number) => `${Number(n || 0)}%`;

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Commission · {invoice ? invoiceNo(invoice) : ""}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && !preview && <p className="text-muted">Calculating…</p>}
        {preview && (
          <>
            {preview.affiliate ? (
              <p className="mb-2"><b>Affiliate:</b> {preview.affiliate.name || "-"}{preview.affiliate.company ? ` (${preview.affiliate.company})` : ""}</p>
            ) : null}
            {preview.tier ? <p className="mb-2"><b>Tier:</b> {preview.tier.title} · {pct(preview.rate)}</p> : null}
            <p className="mb-2"><b>Invoice base amount:</b> {inr2(preview.saleAmount)}</p>
            <p className="mb-2"><b>Commission:</b> {inr2(preview.commissionAmount)}</p>
            {!preview.eligible && (
              <p className="text-danger mb-0">{preview.reason || "Not eligible for commission"}</p>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>Close</Button>
        <Button variant="primary" onClick={approve} disabled={loading || !preview?.eligible}>
          {loading ? "Processing…" : "Approve commission"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default InvoiceCommissionModal;
